/**
 * Búsqueda global Cmd+K — overlay tipo Spotlight.
 * Llama a /api/v1/search?q=... mientras escribes (debounced 200ms).
 */
import { apiClient } from '../lib/api.js';

const h = (tag, attrs = {}, children = []) => {
  const el = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (v == null || v === false) continue;
    if (k === 'class') el.className = v;
    else if (k.startsWith('on') && typeof v === 'function') el.addEventListener(k.slice(2).toLowerCase(), v);
    else if (k === 'html') el.innerHTML = v;
    else if (v === true) el.setAttribute(k, '');
    else el.setAttribute(k, v);
  }
  for (const c of [].concat(children)) {
    if (c == null || c === false) continue;
    el.appendChild(typeof c === 'string' ? document.createTextNode(c) : c);
  }
  return el;
};

let overlayEl = null;
let debounceTimer = null;
let resultsCache = [];
let activeIdx = 0;

export function setupCmdK(onNavigate) {
  document.addEventListener('keydown', (e) => {
    // Cmd+K / Ctrl+K
    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
      e.preventDefault();
      if (overlayEl) closeOverlay();
      else openOverlay(onNavigate);
    }
    // Escape para cerrar
    if (e.key === 'Escape' && overlayEl) closeOverlay();
  });
}

function openOverlay(onNavigate) {
  resultsCache = [];
  activeIdx = 0;
  overlayEl = h('div', {
    class: 'fixed inset-0 bg-base-900/80 backdrop-blur z-[60] flex items-start justify-center pt-[10vh] p-4',
    onclick: (e) => { if (e.target === overlayEl) closeOverlay(); },
  });

  const panel = h('div', { class: 'bg-base-800 border border-gold-400/40 rounded-2xl max-w-xl w-full shadow-soft overflow-hidden' });
  const input = h('input', {
    type: 'text',
    placeholder: 'Buscar notas, tareas, inmuebles, vehículos…',
    class: 'w-full bg-transparent border-0 px-5 py-4 text-ink-100 text-base placeholder-ink-400 focus:outline-none border-b border-base-700',
    onkeydown: (e) => {
      if (e.key === 'ArrowDown') { e.preventDefault(); navigateResults(1); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); navigateResults(-1); }
      else if (e.key === 'Enter') { e.preventDefault(); selectActive(onNavigate); }
    },
    oninput: (e) => debouncedSearch(e.target.value),
  });
  const resultsEl = h('div', { id: 'cmdk-results', class: 'max-h-[60vh] overflow-y-auto' });
  const hint = h('div', { class: 'text-[10px] text-ink-400 px-5 py-2 border-t border-base-700 flex gap-3' }, [
    h('span', {}, '↑↓ navegar'),
    h('span', {}, '⏎ abrir'),
    h('span', {}, 'esc cerrar'),
  ]);
  panel.appendChild(input);
  panel.appendChild(resultsEl);
  panel.appendChild(hint);
  overlayEl.appendChild(panel);
  document.body.appendChild(overlayEl);
  setTimeout(() => input.focus(), 50);
  renderResults([]);
}

function closeOverlay() {
  if (overlayEl) {
    overlayEl.remove();
    overlayEl = null;
  }
}

function debouncedSearch(q) {
  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = setTimeout(async () => {
    if (!q || q.trim().length < 2) {
      renderResults([]);
      return;
    }
    try {
      const r = await apiClient.get(`/api/v1/search?q=${encodeURIComponent(q.trim())}`);
      resultsCache = r.results || [];
      activeIdx = 0;
      renderResults(resultsCache);
    } catch {
      renderResults([]);
    }
  }, 200);
}

function renderResults(items) {
  const el = document.getElementById('cmdk-results');
  if (!el) return;
  el.innerHTML = '';
  if (items.length === 0) {
    el.appendChild(h('div', { class: 'p-6 text-center text-ink-400 text-sm' }, 'Escribe al menos 2 caracteres…'));
    return;
  }
  items.forEach((it, idx) => {
    const isActive = idx === activeIdx;
    const row = h('div', {
      class: `px-5 py-3 cursor-pointer flex items-center gap-3 ${isActive ? 'bg-gold-400/10' : 'hover:bg-base-700/50'}`,
      onclick: () => selectResult(it),
    }, [
      h('span', { class: 'text-xl' }, emojiForType(it.type)),
      h('div', { class: 'flex-1 min-w-0' }, [
        h('div', { class: 'text-sm font-medium truncate' }, it.label),
        h('div', { class: 'text-xs text-ink-400' }, `${it.type}${it.meta ? ' · ' + it.meta : ''}`),
      ]),
      h('span', { class: 'text-[10px] text-ink-400 uppercase tracking-wider' }, it.section),
    ]);
    el.appendChild(row);
  });
}

function navigateResults(dir) {
  if (resultsCache.length === 0) return;
  activeIdx = (activeIdx + dir + resultsCache.length) % resultsCache.length;
  renderResults(resultsCache);
  const el = document.querySelectorAll('#cmdk-results > div')[activeIdx];
  el?.scrollIntoView({ block: 'nearest' });
}

let _onNavigate = null;
function selectActive(onNavigate) {
  _onNavigate = onNavigate;
  const it = resultsCache[activeIdx];
  if (it) selectResult(it);
}

function selectResult(it) {
  closeOverlay();
  if (_onNavigate) _onNavigate(it.section);
}

function emojiForType(t) {
  return { note: '📝', task: '✅', property: '🏘️', vehicle: '🚗', project: '📋', goal: '🎯' }[t] || '🔹';
}
